/**
 * Question Bank combobox: search-or-create entry point for question blocks.
 *
 * Loads the availability-scoped question list for the block's type on demand,
 * searches the complete bank on the server, and appends result pages while
 * the author scrolls. In client context it splits into two pool tabs (the
 * account's own bank and Emtrain Standard), queried simultaneously so both
 * tabs' counts stay live from one search. Creation is offered via a
 * link below the search box that is always visible (NA-406), independent of
 * the search field and not a row inside the scrollable results.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import type {
  QuestionBlockType,
  QuestionDefinitionDto,
} from '@course-authoring/shared-types';
import { useTheme } from '@course-authoring/shared-types';

import { useInfiniteQuestions } from '../../api/questionApi';
import { formatQuestionHumanId } from '../../utils/humanIds';
import { questionBlockTypeLabel } from './questionBlockConversion';
import { toScopeAccountId, useClientContext } from '../../hooks/useClientContext';
import { getAccountLabel } from '../layout/ClientContextPicker';

const STANDARD_TAB_LABEL = 'Emtrain Standard';
type PoolTab = 'account' | 'standard';

const SEARCH_DEBOUNCE_MS = 300;
const DROPDOWN_MAX_HEIGHT = 260;
const DROPDOWN_GAP = 4;
const VIEWPORT_MARGIN = 8;

/**
 * Fixed-position coordinates for the portaled dropdown. Anchored below the input
 * by default, flipped above (`bottom`) when there isn't room below — mirrors the
 * viewport-aware placement used by BlockTypeChooser/AddBlockButton.
 */
type DropdownPlacement = { left: number; width: number; maxHeight: number } & (
  | { top: number }
  | { bottom: number }
);

export interface QuestionComboboxProps {
  blockType: QuestionBlockType;
  onSelect: (dto: QuestionDefinitionDto) => void;
  onCreateNew: (searchText: string) => void;
}

export function QuestionCombobox({
  blockType,
  onSelect,
  onCreateNew,
}: QuestionComboboxProps): React.ReactElement {
  const { tokens } = useTheme();
  const { clientContext } = useClientContext();
  const scopeAccountId = toScopeAccountId(clientContext?.id);
  const scopeAccountName = clientContext && scopeAccountId !== null ? getAccountLabel(clientContext) : null;
  const isClientContext = scopeAccountId !== null;

  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [placement, setPlacement] = useState<DropdownPlacement | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [activeTab, setActiveTab] = useState<PoolTab>('account');
  // NA-406 review fix: each tab keeps its own remembered scroll offset — both
  // tabs render into the same scrollable `dropdownRef` container, so without
  // this the raw scroll position carries over from whichever tab was active
  // before, instead of each tab preserving its own browsing position.
  const scrollPositionsRef = useRef<Record<PoolTab, number>>({ account: 0, standard: 0 });

  const typeLabel = questionBlockTypeLabel(blockType);
  const trimmedSearch = search.trim();
  const trimmedDebouncedSearch = debouncedSearch.trim();

  // Opening the combobox always defaults to the account's own tab in client
  // context (per the confirmed prototype), regardless of which tab was last active.
  useEffect(() => {
    if (isOpen && isClientContext) {
      setActiveTab('account');
      scrollPositionsRef.current = { account: 0, standard: 0 };
    }
  }, [isOpen, isClientContext]);

  // Restore the newly-active tab's own remembered scroll offset after its
  // results render, rather than leaving whatever offset the previous tab left.
  useLayoutEffect(() => {
    const dropdown = dropdownRef.current;
    if (!dropdown) return;
    dropdown.scrollTop = scrollPositionsRef.current[activeTab];
  }, [activeTab]);

  const accountQuery = useInfiniteQuestions({
    blockType,
    pageSize: 25,
    search: trimmedDebouncedSearch,
    externalAccountId: scopeAccountId ?? undefined,
  }, { enabled: isOpen && isClientContext });

  const standardQuery = useInfiniteQuestions({
    blockType,
    pageSize: 25,
    search: trimmedDebouncedSearch,
    externalAccountId: null,
  }, { enabled: isOpen });

  // In Standard context there is only ever one pool; in client context the
  // active tab picks which of the two simultaneously-fetched queries drives
  // the visible list, while both keep updating so tab counts stay live.
  const activeQuery = isClientContext && activeTab === 'standard' ? standardQuery : isClientContext ? accountQuery : standardQuery;

  const {
    data,
    isLoading,
    isError,
    isFetchNextPageError,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = activeQuery;
  const questions = useMemo(
    () => data?.pages.flatMap((page) => page.items) ?? [],
    [data],
  );

  const accountCount = accountQuery.data?.pages[0]?.totalCount ?? 0;
  const standardCount = standardQuery.data?.pages[0]?.totalCount ?? 0;
  const inactiveCount = isClientContext && activeTab === 'standard' ? accountCount : standardCount;
  const inactiveTabLabel = isClientContext && activeTab === 'standard'
    ? (scopeAccountName ?? 'Account')
    : STANDARD_TAB_LABEL;

  // Debounce the filter term (the create link tracks the raw input immediately).
  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [search]);

  // Anchor the portaled dropdown to the input and keep it aligned as the canvas
  // scrolls or the window resizes. The dropdown is rendered into document.body
  // with fixed positioning so it escapes the block card's `overflow: hidden`
  // (which was clipping it) and stacks above sibling blocks.
  useLayoutEffect(() => {
    if (!isOpen) return;
    const updatePosition = () => {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_MARGIN;
      const spaceAbove = rect.top - VIEWPORT_MARGIN;
      if (spaceBelow >= DROPDOWN_MAX_HEIGHT || spaceBelow >= spaceAbove) {
        setPlacement({
          left: rect.left,
          width: rect.width,
          top: rect.bottom + DROPDOWN_GAP,
          maxHeight: Math.min(DROPDOWN_MAX_HEIGHT, spaceBelow),
        });
      } else {
        setPlacement({
          left: rect.left,
          width: rect.width,
          bottom: window.innerHeight - rect.top + DROPDOWN_GAP,
          maxHeight: Math.min(DROPDOWN_MAX_HEIGHT, spaceAbove),
        });
      }
    };
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  // Close on outside click (the dropdown lives in a portal, so check both nodes).
  useEffect(() => {
    if (!isOpen) return;
    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      const inInput = containerRef.current?.contains(target) ?? false;
      const inDropdown = dropdownRef.current?.contains(target) ?? false;
      if (!inInput && !inDropdown) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isOpen]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const root = dropdownRef.current;
    if (
      !isOpen
      || !sentinel
      || !root
      || isFetchNextPageError
      || typeof IntersectionObserver === 'undefined'
    ) return undefined;

    const observer = new IntersectionObserver((entries) => {
      if (
        entries[0]?.isIntersecting
        && hasNextPage
        && !isFetchingNextPage
        && !isFetchNextPageError
      ) {
        void fetchNextPage();
      }
    }, { root, threshold: 0.1 });

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchNextPageError, isFetchingNextPage, isOpen, placement]);

  const hasDebouncedTerm = debouncedSearch.trim() !== '';

  const styles = useMemo((): Record<string, CSSProperties> => ({
    container: {
      position: 'relative',
      width: '100%',
    },
    input: {
      width: '100%',
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.sm,
      padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
      fontFamily: tokens.typography.fontFamily,
      fontSize: tokens.typography.body.size,
      color: tokens.text.primary,
      outline: 'none',
      backgroundColor: tokens.backgrounds.app,
      boxSizing: 'border-box',
    },
    dropdown: {
      position: 'fixed',
      backgroundColor: tokens.backgrounds.app,
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.md,
      boxShadow: tokens.shadow.md,
      overflowY: 'auto',
      zIndex: 100,
      display: 'flex',
      flexDirection: 'column',
    },
    createLink: {
      display: 'block',
      textAlign: 'left',
      background: 'none',
      border: 'none',
      padding: `${tokens.spacing.xs} 0`,
      fontFamily: tokens.typography.fontFamily,
      fontSize: tokens.typography.body.size,
      fontWeight: 500,
      color: tokens.text.link,
      cursor: 'pointer',
    },
    tabBar: {
      display: 'flex',
      position: 'sticky',
      top: 0,
      zIndex: 1,
      background: tokens.backgrounds.app,
      borderBottom: `1px solid ${tokens.borders.default}`,
    },
    tab: {
      flex: 1,
      textAlign: 'center',
      background: 'none',
      border: 'none',
      borderBottom: '2px solid transparent',
      padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
      fontFamily: tokens.typography.fontFamily,
      fontSize: tokens.typography.body.size,
      color: tokens.text.muted,
      cursor: 'pointer',
    },
    tabActive: {
      color: tokens.brand.primary,
      fontWeight: 500,
      borderBottom: `2px solid ${tokens.brand.primary}`,
    },
    questionRow: {
      display: 'block',
      width: '100%',
      textAlign: 'left',
      background: 'none',
      border: 'none',
      padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
      fontFamily: tokens.typography.fontFamily,
      fontSize: tokens.typography.body.size,
      color: tokens.text.primary,
      cursor: 'pointer',
    },
    mutedRow: {
      padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
      fontFamily: tokens.typography.fontFamily,
      fontSize: tokens.typography.body.size,
      fontStyle: 'italic',
      color: tokens.text.muted,
    },
    loadMoreButton: {
      display: 'block',
      width: '100%',
      border: 'none',
      borderTop: `1px solid ${tokens.borders.default}`,
      padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
      backgroundColor: tokens.backgrounds.app,
      color: tokens.brand.primary,
      cursor: 'pointer',
      fontFamily: tokens.typography.fontFamily,
      fontSize: tokens.typography.body.size,
    },
    retryButton: {
      border: 'none',
      background: 'none',
      color: tokens.brand.primary,
      cursor: 'pointer',
      fontFamily: tokens.typography.fontFamily,
      fontSize: tokens.typography.body.size,
      textDecoration: 'underline',
      padding: 0,
    },
    sentinel: {
      height: '1px',
    },
  }), [tokens]);

  const createRowLabel = `+ Create a new ${typeLabel} question`;

  // Creating is a separate action from searching, so the search text is not carried over.
  const handleCreateNew = () => {
    setIsOpen(false);
    onCreateNew('');
  };

  const handleSelect = (dto: QuestionDefinitionDto) => {
    setIsOpen(false);
    onSelect(dto);
  };

  return (
    <div ref={containerRef} style={styles.container}>
      <input
        type="text"
        role="combobox"
        aria-expanded={isOpen}
        aria-label={`Search ${typeLabel} questions`}
        placeholder={`Search existing ${typeLabel} questions…`}
        value={search}
        onChange={(event) => {
          setSearch(event.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.stopPropagation();
            setIsOpen(false);
          }
        }}
        style={styles.input}
      />

      <button
        type="button"
        style={styles.createLink}
        onClick={handleCreateNew}
        onMouseEnter={(event) => {
          event.currentTarget.style.backgroundColor = tokens.backgrounds.surface;
        }}
        onMouseLeave={(event) => {
          event.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        {createRowLabel}
      </button>

      {isOpen && placement && createPortal(
        <div
          ref={dropdownRef}
          style={{ ...styles.dropdown, ...placement }}
          onScroll={(event) => {
            scrollPositionsRef.current[activeTab] = event.currentTarget.scrollTop;
          }}
        >
          {isClientContext && (
            <div style={styles.tabBar} role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'account'}
                style={{ ...styles.tab, ...(activeTab === 'account' ? styles.tabActive : {}) }}
                onClick={() => setActiveTab('account')}
              >
                {`${scopeAccountName ?? 'Account'} (${accountCount})`}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'standard'}
                style={{ ...styles.tab, ...(activeTab === 'standard' ? styles.tabActive : {}) }}
                onClick={() => setActiveTab('standard')}
              >
                {`${STANDARD_TAB_LABEL} (${standardCount})`}
              </button>
            </div>
          )}

          {isLoading ? (
            <div style={styles.mutedRow} role="status" aria-live="polite">Loading questions…</div>
          ) : isError && questions.length === 0 ? (
            <div style={styles.mutedRow} role="status">
              Failed to load questions.{' '}
              <button type="button" style={styles.retryButton} onClick={() => { void refetch(); }}>
                Retry
              </button>
            </div>
          ) : questions.length === 0 && hasDebouncedTerm && isClientContext && inactiveCount > 0 ? (
            <div style={styles.mutedRow} role="status">
              {`No matching questions found here. ${inactiveCount} match${inactiveCount === 1 ? '' : 'es'} found in ${inactiveTabLabel}.`}
            </div>
          ) : questions.length === 0 && hasDebouncedTerm ? (
            <div style={styles.mutedRow} role="status">No matching questions found.</div>
          ) : questions.length === 0 ? (
            <div style={styles.mutedRow} role="status">No questions available.</div>
          ) : (
            <>
              {questions.map((dto) => (
                <button
                  key={dto.questionDefinitionId}
                  type="button"
                  style={styles.questionRow}
                  onClick={() => handleSelect(dto)}
                  onMouseEnter={(event) => {
                    event.currentTarget.style.backgroundColor = tokens.backgrounds.surface;
                  }}
                  onMouseLeave={(event) => {
                    event.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  {`${formatQuestionHumanId(dto.questionDefinitionId)} · ${dto.questionTextPlain}`}
                </button>
              ))}

              {isFetchNextPageError && (
                <div style={styles.mutedRow} role="status">
                  Failed to load more questions.{' '}
                  <button type="button" style={styles.retryButton} onClick={() => { void fetchNextPage(); }}>
                    Retry
                  </button>
                </div>
              )}

              {hasNextPage ? (
                <>
                  <div ref={sentinelRef} style={styles.sentinel} aria-hidden="true" />
                  <button
                    type="button"
                    style={styles.loadMoreButton}
                    onClick={() => { void fetchNextPage(); }}
                    disabled={isFetchingNextPage}
                  >
                    {isFetchingNextPage ? 'Loading more questions…' : 'Load more'}
                  </button>
                </>
              ) : (
                <div style={styles.mutedRow} role="status">All questions loaded.</div>
              )}
            </>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}

QuestionCombobox.displayName = 'QuestionCombobox';
